/// <reference path='rounding.service.ts' />

module Meevo.Components.Common {

    export interface IInventoryRoundingService extends IRoundingService {
        resetDecimalPositions();
        setDecimalPositions(decPos: number);
        subtract(value1: number, value2: number, decimalPositions?: number): number;
        add(value1: number, value2: number, decimalPositions?: number): number;
        multiply(value1: number, value2: number, decimalPositions?: number): number;
        divide(value1: number, value2: number, decimalPositions?: number): number;
        subtractNormalizedNumbers(value1: number, value2: number, decimalPositions?: number): number;
        addNormalizedNumbers(value1: number, value2: number, decimalPositions?: number): number;
        multiplyNormalizedNumbers(value1: number, value2: number, decimalPositions?: number): number;
        divideNormalizedNumbers(value1: number, value2: number, decimalPositions?: number): number;
        performOperationOnNormalizedNumbers(value1: number, value2: number, op: Function, decimalPositions?: number): number;
    }

    export class InventoryRoundingService extends RoundingService implements IInventoryRoundingService {

        constructor(
            protected identityService: Meevo.Components.Common.IIdentityService
        ) {
            super(identityService);
        }

        resetDecimalPositions() {
            let identity = this.identityService.current();
            this.decimalPositions = identity.decimalPositions || 2;
            this.normalize = "e" + this.decimalPositions;
            this.normalizeDecimal = "e-" + this.decimalPositions; 
        }

        setDecimalPositions(decPos: number) {
            this.decimalPositions = decPos || this.decimalPositions;
            this.normalize = "e" + this.decimalPositions;
            this.normalizeDecimal = "e-" + this.decimalPositions;
        }

        subtract(value1: number, value2: number, decimalPositions?: number): number {
            return this.subtractNormalizedNumbers(value1, value2, decimalPositions);
        }

        add(value1: number, value2: number, decimalPositions?: number): number {
            return this.addNormalizedNumbers(value1, value2, decimalPositions);
        }

        multiply(value1: number, value2: number, decimalPositions?: number): number {
            return this.multiplyNormalizedNumbers(value1, value2, decimalPositions);
        }

        divide(value1: number, value2: number, decimalPositions?: number): number {
            return this.divideNormalizedNumbers(value1, value2, decimalPositions);
        }

        subtractNormalizedNumbers(value1: number, value2: number, decimalPositions?: number): number {
            return this.performOperationOnNormalizedNumbers(value1, value2, (a, b) => a - b, decimalPositions);
        }

        addNormalizedNumbers(value1: number, value2: number, decimalPositions?: number): number {
            return this.performOperationOnNormalizedNumbers(value1, value2, (a, b) => a + b, decimalPositions);
        }

        multiplyNormalizedNumbers(value1: number, value2: number, decimalPositions?: number): number {
            let multiplier = this.normalizeDecimal;
            if (typeof decimalPositions !== 'undefined' && decimalPositions !== null) {
                if (decimalPositions < 0) {
                    throw new Error("decimalPositions must be a positive number");
                }
                multiplier = "e-" + decimalPositions;
            }
            return +(this.performOperationOnNormalizedNumbers(value1, value2, (a, b) => a * b, decimalPositions) + multiplier);
        }

        divideNormalizedNumbers(value1: number, value2: number, decimalPositions?: number): number {
            return (this.normalizeNumber(value1, decimalPositions) / this.normalizeNumber(value2, decimalPositions));
        }

        performOperationOnNormalizedNumbers(value1: number, value2: number, op: Function, decimalPositions?: number): number {
            let multiplier = this.normalizeDecimal;
            if (typeof decimalPositions !== 'undefined' && decimalPositions !== null) {
                if (decimalPositions < 0) {
                    throw new Error("decimalPositions must be a positive number");
                }
                multiplier = "e-" + decimalPositions;
            }
            return +(op(this.normalizeNumber(value1, decimalPositions), this.normalizeNumber(value2, decimalPositions)) + multiplier);
        }

        static factory(): Function {
            const svc = (
                identityService
            ) => new InventoryRoundingService(
                identityService
            );

            svc.$inject = [
                'meevo.components.common.IdentityService',
            ];

            return svc;
        }
    }

    angular.module("meevo.components.common")
        .factory("meevo.components.common.InventoryRoundingService", InventoryRoundingService.factory());
}